import React, { forwardRef, useCallback, useEffect } from 'react';
import PlusSmall from '@uber/base-icons/plus-small';
import { styled } from 'baseui';
import { Button, KIND as BUTTON_KIND, SHAPE } from 'baseui/button';
import { KIND, Tag } from 'baseui/tag';
import useTutorial from '../../image/labelling/providers/hooks/use-tutorials';
import { TUTORIAL_TYPES } from '../tutorial-list/constants';
import { TUTORIAL_DISPLAY_NAME } from '../tutorial-list/constants';
import type { AnnotationLabelT, ShowNotificationT } from '../types/types';
import type { SelectLabelPayloadT } from './analytics/types';
import useAnalyticsForSidePanel from './analytics/useAnalyticsForSidePanel';
import { changeLabelColor } from './tutorials/label-color';
import { LABEL_COLOR_PICKER_ID } from './constants';
import { SelectableListItemContainer, SWATCH_CIRCLE_SIZE, Title, TITLE_LEFT_MARGIN } from './styled-components';
const OuterContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: 1
});
const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column'
});
const Required = styled('span', ({
  $theme
}) => ({
  ...$theme.typography.LabelLarge,
  color: $theme.colors.negative
}));
const Content = styled('div', () => ({
  display: 'flex',
  alignItems: 'center'
}));
const ConstraintContainer = styled('div', {
  marginLeft: `${TITLE_LEFT_MARGIN + SWATCH_CIRCLE_SIZE}px`
});
const ENTER_KEY = 'Enter';
type PropsT = {
  onAnnotationClick: () => void;
  annotationLabel: AnnotationLabelT;
  selectedAnnotationLabelId?: number;
  isSelected: boolean;
  onAddAnnotationLabel?: () => void;
  isChild?: boolean;
  showNotification?: ShowNotificationT;
};
const AnnotationLabel = (props: PropsT, ref) => {
  const {
    onAnnotationClick: _onAnnotationClick
  } = props;
  const {
    sendEventForLabelSelection
  } = useAnalyticsForSidePanel();
  const label = props.annotationLabel?.title;
  const onAnnotationLabelClick = useCallback(() => {
    _onAnnotationClick();
    sendEventForLabelSelection(({
      label
    } as SelectLabelPayloadT));
  }, [_onAnnotationClick, label, sendEventForLabelSelection]);
  const onKeyPress = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ENTER_KEY) {
      onAnnotationLabelClick();
    }
  }, [onAnnotationLabelClick]);
  const {
    addStep,
    onTutorialReplay
  } = useTutorial();
  useEffect(() => {
    const cleanUp = onTutorialReplay(changeLabelColor.id, TUTORIAL_TYPES.SIDE_PANEL, TUTORIAL_DISPLAY_NAME.label, () => {
      addStep({
        ...changeLabelColor,
        // @ts-expect-error todo(ts-migration) TS2322 Type '() => Element | undefined' is not assignable to type '() => Element | null'.
        getTarget: () => {
          return props.selectedAnnotationLabelId ? document.querySelectorAll(`[data-selector-id=${LABEL_COLOR_PICKER_ID}]`)[props.selectedAnnotationLabelId] : document.querySelectorAll(`[data-selector-id=${LABEL_COLOR_PICKER_ID}]`)[0];
        },
        isReplay: true
      }, changeLabelColor.id);
    });
    return cleanUp;
  }, [addStep, onTutorialReplay, props.selectedAnnotationLabelId]);
  return <SelectableListItemContainer data-testid={`annotation-label-${props.annotationLabel.title}`} onClick={onAnnotationLabelClick} onKeyDown={onKeyPress} $isSelected={props.isSelected} $isChild={props.isChild} role="button" tabIndex={0} ref={ref}>
      <OuterContainer>
        <Container>
          <Content>
            <Title>
              {props.annotationLabel.isRequired && <Required>*</Required>}{' '}
              {props.annotationLabel.title}
            </Title>
          </Content>
          {props.annotationLabel.max !== undefined && <ConstraintContainer>
              <Tag kind={KIND.accent} closeable={false}>
                Max: {props.annotationLabel.max}
              </Tag>
            </ConstraintContainer>}
        </Container>
        {props.onAddAnnotationLabel && <Button data-testid={`annotation-label-${props.annotationLabel.title}-add-btn`} style={{
        width: '24px',
        height: '24px'
      }} kind={BUTTON_KIND.secondary} shape={SHAPE.circle} onClick={e => {
        e.stopPropagation();
        props.onAddAnnotationLabel?.();
      }}>
            <PlusSmall />
          </Button>}
      </OuterContainer>
    </SelectableListItemContainer>;
};
export default forwardRef(AnnotationLabel);